// LogRocket session recording + client-side observability.
//
// Gated on `import.meta.env.PROD` AND the presence of a VITE_LOGROCKET_APP_ID
// env var — so local dev + preview builds stay out of the quota, and we can
// kill LogRocket in prod by simply unsetting the env var (no code ship needed).
//
// The snippet in LogRocket's docs calls `LogRocket.init('mtg-oracle/mtg-oracle')`
// at module import time. We wrap it in `initLogRocket()` so the call is
// idempotent and the app ID comes from env rather than hardcoded source.
//
// User identification lives in useLogRocketIdentify() below — mounted once
// inside <SignedIn> so sessions get correlated to a Clerk user the moment
// that user loads, and stay anonymous otherwise.
//
// Default PII posture: rely on LogRocket's built-in input/password sanitizers.
// We don't add custom scrubbers over brief text or agent answers — that's
// user-facing deck reasoning, not sensitive.

import LogRocket from "logrocket";
import { useUser } from "@clerk/clerk-react";
import { useEffect } from "react";

const APP_ID = import.meta.env.VITE_LOGROCKET_APP_ID as string | undefined;

let initialized = false;

export function initLogRocket(): void {
  if (initialized) return;
  if (!import.meta.env.PROD) return;
  if (!APP_ID) return;
  LogRocket.init(APP_ID);
  initialized = true;
}

/** True once init() actually ran. Lets callers skip identify/track work
 *  when LogRocket is inactive (dev, missing env var). */
export function isLogRocketActive(): boolean {
  return initialized;
}

/**
 * Mount once inside <SignedIn>. Calls LogRocket.identify when Clerk's user
 * record is available so session recordings tie back to a specific user.
 * No-op when LogRocket isn't active (dev, missing env var).
 */
export function useLogRocketIdentify(): void {
  const { isLoaded, user } = useUser();
  useEffect(() => {
    if (!isLogRocketActive()) return;
    if (!isLoaded || !user) return;
    LogRocket.identify(user.id, {
      name: user.fullName ?? user.username ?? "",
      email: user.primaryEmailAddress?.emailAddress ?? "",
    });
  }, [isLoaded, user]);
}
